/**
 * Shift Comparison Hooks (Phase 25.3)
 *
 * TanStack Query hooks for the manager-facing shift comparison analytics:
 * - useShiftComparison(params): list of shift cards with sparklines + deltaVsPeers + topDrivers
 * - useShiftComparisonDrillDown(shiftId, params): per-section + operator leaderboard
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// QUERY KEYS
// ============================================

export const shiftComparisonKeys = {
  all: ['shift-comparison'] as const,
  list: (params: ShiftComparisonParams) =>
    [...shiftComparisonKeys.all, 'list', params] as const,
  drillDown: (shiftId: string, params: ShiftComparisonParams) =>
    [...shiftComparisonKeys.all, 'drill-down', shiftId, params] as const,
};

// ============================================
// TYPES
// ============================================

export interface ShiftComparisonParams {
  sopId?: string;
  stationId?: string; // UI: "Zone" (DB: stations)
  departmentId?: string;
  startDate?: string; // ISO string
  endDate?: string; // ISO string
}

export interface SparklinePoint {
  date: string; // YYYY-MM-DD
  avgScore: number | null;
  count: number;
}

export interface ShiftCard {
  shiftId: string;
  shiftName: string;
  shiftCode: string | null;
  rank: number;
  avgScore: number | null;
  avgCompliance: number | null;
  avgEfficiency: number | null;
  avgSafety: number | null;
  assessmentCount: number;
  operatorCount: number;
  deltaVsPeers: number | null;
  sparkline: SparklinePoint[];
}

export interface TopDriver {
  sectionId: string;
  sectionTitle: string;
  variance: number;
  minScore: number;
  maxScore: number;
  shiftSpread: number;
}

export interface ShiftComparisonResponse {
  dateRange: { startDate: string; endDate: string };
  filters: {
    sopId: string | null;
    stationId: string | null;
    departmentId: string | null;
  };
  comparison: ShiftCard[];
  topDrivers: TopDriver[];
  summary: {
    totalShifts: number;
    shiftsWithData: number;
    totalAssessments: number;
    totalOperators: number;
    overallAverage: number | null;
  };
}

export interface DrillDownSection {
  sectionId: string;
  sectionTitle: string;
  sopId: string;
  sopTitle: string;
  avgScore: number | null;
  avgCompliance: number | null;
  avgEfficiency: number | null;
  avgSafety: number | null;
  assessmentCount: number;
  operatorCount: number;
}

export interface DrillDownOperator {
  rank: number;
  operatorId: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  avgScore: number | null;
  avgCompliance: number | null;
  avgEfficiency: number | null;
  avgSafety: number | null;
  assessmentCount: number;
}

export interface ShiftDrillDownResponse {
  shift: { id: string; name: string; code: string | null };
  dateRange: { startDate: string; endDate: string };
  filters: {
    sopId: string | null;
    stationId: string | null;
    departmentId: string | null;
  };
  sections: DrillDownSection[];
  operatorLeaderboard: DrillDownOperator[];
  summary: {
    totalSections: number;
    totalOperators: number;
    totalAssessments: number;
    shiftAvgScore: number | null;
  };
}

// ============================================
// HELPERS
// ============================================

function buildSearchParams(params: ShiftComparisonParams): string {
  const qs = new URLSearchParams();
  if (params.sopId) qs.set('sopId', params.sopId);
  if (params.stationId) qs.set('stationId', params.stationId);
  if (params.departmentId) qs.set('departmentId', params.departmentId);
  if (params.startDate) qs.set('startDate', params.startDate);
  if (params.endDate) qs.set('endDate', params.endDate);
  return qs.toString();
}

// ============================================
// HOOKS
// ============================================

export function useShiftComparison(params: ShiftComparisonParams) {
  const api = useApi();

  return useQuery({
    queryKey: shiftComparisonKeys.list(params),
    queryFn: async () => {
      const qs = buildSearchParams(params);
      const url = qs
        ? `/api/analytics/shift-comparison?${qs}`
        : '/api/analytics/shift-comparison';
      return api.get<ShiftComparisonResponse>(url);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useShiftComparisonDrillDown(
  shiftId: string | undefined,
  params: ShiftComparisonParams
) {
  const api = useApi();

  return useQuery({
    queryKey: shiftComparisonKeys.drillDown(shiftId ?? '', params),
    queryFn: async () => {
      const qs = buildSearchParams(params);
      const base = `/api/analytics/shift-comparison/${shiftId}/sections`;
      const url = qs ? `${base}?${qs}` : base;
      return api.get<ShiftDrillDownResponse>(url);
    },
    enabled: !!shiftId,
    staleTime: 5 * 60 * 1000,
  });
}
